/**
 * /accounting/reports/cash-forecast — 13-Week Cash Forecast.
 *
 * Live projection of weekly cash position from AR (probability-weighted),
 * scheduled AP due dates, and active recurring JEs. Highlights the lowest
 * projected balance and the week it hits.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Badge } from "@/components/ui/badge";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAP } from "@/data/accounting/ap/APContext";
import { useRecurringJournals } from "@/data/accounting/recurring/RecurringJournalContext";
import { buildCashForecast } from "@/data/accounting/forecast/cashForecast";
import { getCustomerARList } from "@/data/accounting/ar/arAging";

const fmt = (n: number) => (n < 0 ? "-" : "") + "$" + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (iso: string) => new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" });

export default function CashForecastPage() {
  const { journalEntries } = useGeneralLedger();
  const { bills } = useAP();
  const { templates } = useRecurringJournals();

  const forecast = useMemo(() => {
    const asOf = new Date().toISOString().slice(0, 10);
    const customerAr = getCustomerARList(journalEntries, asOf);
    return buildCashForecast({
      asOf,
      journalEntries,
      openBills: bills,
      customerAr,
      recurringTemplates: templates,
    });
  }, [journalEntries, bills, templates]);

  // Sparkline path over closing balances
  const sparkline = useMemo(() => {
    const xs = forecast.weeks.map(w => w.closing);
    if (xs.length === 0) return { path: "", min: 0, max: 0 };
    const min = Math.min(...xs, 0);
    const max = Math.max(...xs, 0);
    const span = Math.max(1, max - min);
    const w = 600, h = 80;
    const stepX = w / Math.max(1, xs.length - 1);
    const path = xs.map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / span) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    const zeroY = h - ((0 - min) / span) * h;
    return { path, min, max, w, h, zeroY };
  }, [forecast]);

  const minWeek = forecast.minProjectedWeekIndex >= 0
    ? forecast.weeks[forecast.minProjectedWeekIndex]
    : null;

  return (
    <CockpitPage>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Link to="/accounting" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Books
        </Link>
        <Badge variant="outline" className="text-xs">As of {fmtDate(forecast.asOf)}</Badge>
      </div>
      <BooksNavRail />

      <div className="mb-4">
        <h1 data-tour-id="cash-forecast-grid" className="text-xl font-medium tracking-tight">13-Week Cash Forecast</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Probability-weighted AR + scheduled AP + recurring JEs. Updates live as the books move.
        </p>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <KpiCard icon={<Wallet className="h-4 w-4" />} label="Starting cash" value={fmt(forecast.startingCash)} tone="neutral" />
          <KpiCard icon={<TrendingUp className="h-4 w-4" />} label="13-wk expected inflow" value={fmt(forecast.totalExpectedInflow)} tone="positive" />
          <KpiCard icon={<TrendingDown className="h-4 w-4" />} label="13-wk scheduled outflow" value={fmt(forecast.totalScheduledOutflow + forecast.totalRecurringOutflow)} tone="negative" />
          <KpiCard
            icon={<AlertTriangle className="h-4 w-4" />}
            label={minWeek ? `Low point — ${minWeek.weekLabel}` : "Low point"}
            value={fmt(forecast.minProjectedBalance)}
            tone={forecast.hitsZero ? "alert" : forecast.minProjectedBalance < forecast.startingCash * 0.25 ? "warn" : "neutral"}
            sub={minWeek ? `Week of ${fmtDate(minWeek.weekStart)}` : undefined}
          />
        </div>
      </CockpitSection>

      <CockpitSection eyebrow="Trend" title="Closing balance by week" className="mt-6">
        <div className="rounded-md border border-border bg-card p-4">
          <svg viewBox={`0 0 ${sparkline.w} ${sparkline.h}`} className="w-full h-20">
            <line x1="0" y1={sparkline.zeroY} x2={sparkline.w} y2={sparkline.zeroY} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeWidth="0.5" />
            <path d={sparkline.path} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.75" />
          </svg>
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{forecast.weeks[0]?.weekLabel} ({fmtDate(forecast.weeks[0]?.weekStart ?? "")})</span>
            <span>{forecast.weeks[12]?.weekLabel} ({fmtDate(forecast.weeks[12]?.weekStart ?? "")})</span>
          </div>
        </div>
      </CockpitSection>

      <CockpitSection eyebrow="Detail" title="Weekly breakdown" className="mt-6">
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Week</th>
                <th className="text-left px-3 py-2">Starts</th>
                <th className="text-right px-3 py-2">Opening</th>
                <th className="text-right px-3 py-2">+ AR</th>
                <th className="text-right px-3 py-2">− AP due</th>
                <th className="text-right px-3 py-2">− Recurring</th>
                <th className="text-right px-3 py-2">Net</th>
                <th className="text-right px-3 py-2">Closing</th>
              </tr>
            </thead>
            <tbody>
              {forecast.weeks.map((w, i) => {
                const isLow = i === forecast.minProjectedWeekIndex;
                return (
                  <tr key={w.weekStart} className={isLow ? "bg-amber-500/5" : i % 2 === 1 ? "bg-muted/20" : ""}>
                    <td className="px-3 py-1.5 font-medium">{w.weekLabel}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{fmtDate(w.weekStart)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(w.opening)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600">{w.inflowExpected ? fmt(w.inflowExpected) : "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-rose-600">{w.outflowScheduled ? fmt(w.outflowScheduled) : "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-rose-600">{w.outflowRecurring ? fmt(w.outflowRecurring) : "—"}</td>
                    <td className={"px-3 py-1.5 text-right tabular-nums " + (w.net >= 0 ? "text-emerald-600" : "text-rose-600")}>{fmt(w.net)}</td>
                    <td className={"px-3 py-1.5 text-right tabular-nums font-medium " + (w.closing < 0 ? "text-rose-700" : "")}>{fmt(w.closing)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          AR inflows use aging-bucket collection probabilities (current 95% • 1–30 85% • 31–60 65% • 61–90 40% • 90+ 15%) and slot into expected collection weeks.
        </p>
      </CockpitSection>
    </CockpitPage>
  );
}

function KpiCard({ icon, label, value, sub, tone }: {
  icon: React.ReactNode; label: string; value: string; sub?: string;
  tone: "positive" | "negative" | "neutral" | "warn" | "alert";
}) {
  const toneClass = {
    positive: "text-emerald-600",
    negative: "text-rose-600",
    neutral: "text-foreground",
    warn: "text-amber-600",
    alert: "text-rose-700",
  }[tone];
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">{icon}{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
